import { InputError, Notification } from '@schibsted-svp/react-ui';
import classnames from 'classnames/bind';
import { Dropzone } from 'components/video/Dropzone/Dropzone';
import { useState } from 'react';

import { heifFormatsValidatorMessage, supportsImageType } from 'models/image';
import type { Control } from 'react-hook-form';
import { useController } from 'react-hook-form';
import css from './PreviewImage.module.scss';
import type { CategoryData, ImageSource } from './types';

const cln = classnames.bind(css);

type PreviewImageProps = {
    name: 'additional.image' | `additional.images.${number}.url`;
    control: Control<CategoryData>;
};

function getSrc(src: ImageSource) {
    return src instanceof File || src instanceof Blob ? URL.createObjectURL(src) : src;
}

function revokeImageURL({ currentTarget }: { currentTarget: HTMLImageElement }) {
    if (currentTarget.src.startsWith('blob')) {
        URL.revokeObjectURL(currentTarget.src);
    }
}

export function PreviewImage({ name, control }: PreviewImageProps) {
    const {
        field: { onChange, value },
        fieldState: { error },
    } = useController({ name, control });

    const [hovered, setHovered] = useState(false);

    const handleOnChange = async (files: File[]) => {
        const file = files[0];
        const isTypeSupported = await supportsImageType(file?.type);
        const fileType = file?.type;

        if (fileType && !isTypeSupported) {
            const heicWarning = heifFormatsValidatorMessage(fileType);
            Notification.notify.warning(`Image type not supported. ${heicWarning}`);
        } else {
            onChange(file);
        }
    };

    function handleMouseEnter() {
        setHovered(true);
    }

    function handleMouseLeave() {
        setHovered(false);
    }

    function handleOnError() {
        onChange(undefined);
    }

    return (
        <div>
            <div
                className={cln('container', { hovered: value && hovered })}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                {value && (
                    <img
                        className={css.image}
                        src={getSrc(value)}
                        alt=""
                        onLoad={revokeImageURL}
                        onError={handleOnError}
                    />
                )}
                <Dropzone className={cln('dropzone', { hidden: value })} onUpload={handleOnChange} />
            </div>
            <InputError>{error?.message}</InputError>
        </div>
    );
}
